import {
  useState, useEffect, useLayoutEffect, useContext, useRef,
} from 'react'
import PropTypes from 'prop-types'
import { rgba } from 'polished'
import { useSwipeable } from 'react-swipeable'
import styled from '@emotion/styled'

import { ExpandableDrawerContext } from '../../ui/contexts/ExpandableDrawerContext'
import {
  EXPANDABLE_DRAWER_MODE_EXPANDED,
  EXPANDABLE_DRAWER_MODE_PREVIEW,
  EXPANDABLE_DRAWER_ACTION_SWIPE,
  EXPANDABLE_DRAWER_ACTION_CTA,
} from '../../../helpers/const'
import ModeToggleCTA from './components/modeToggleCTA'
import CloseCTA from './components/closeCTA'
import { getLayoutHorizontalPadding } from '../../ui/Layout'

const StyledOverlay = styled.div(({ active, theme: { breakpoints } }) => ({
  display: active ? 'block' : 'none',
  position: 'fixed',
  zIndex: 12,
  left: '0',
  top: '0',
  right: '0',
  bottom: '0',
  overflow: 'hidden',
  background: 'rgba(0, 0, 0, 0.5)',
  [`@media (max-width: ${breakpoints.small})`]: {
    transition: 'display 0.25s',
  },
}))

const DrawerStyle = (open, colors, spaces, breakpoints) => {
  const horizontalPadding = getLayoutHorizontalPadding(true, { spaces, breakpoints })

  return ({
    borderRadius: `${spaces.default}px ${spaces.default}px 0 0`,
    background: colors.white,
    position: 'fixed',
    zIndex: 13,
    bottom: 0,
    left: '50%',
    transform: ' translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'top 0.3s',
    boxShadow: open ? `0px -3px 5px ${rgba(colors.dark_grey, 0.4)}` : 'none',
    height: 'auto',
    maxWidth: breakpoints.xlarge,
    width: `calc(100% - ${horizontalPadding.default * 2}px)`,
    [`@media (min-width: ${breakpoints.small})`]: {
      width: `calc(100% - ${horizontalPadding[breakpoints.small] * 2}px)`,
    },
    [`@media (min-width: ${breakpoints.xlarge})`]: {
      width: `calc(100% - ${horizontalPadding[breakpoints.xlarge] * 2}px)`,
    },
  })
}

const getDrawerTopPosition = (open, spaces, isPreview, isWebView) => {
  if (!open) {
    return '100%'
  }

  if (isPreview) {
    return 'auto'
  }

  if (isWebView) {
    return `${spaces.default / 4}px`
  }

  return `${spaces.large * 5}px`
}

const StyledRoot = styled.div((
  { open, isWebView, theme: { colors, spaces, breakpoints } },
) => ({
  ...DrawerStyle(open, colors, spaces, breakpoints),
  top: getDrawerTopPosition(open, spaces, false, isWebView),
}))

const StyledRootPreview = styled.div((
  { open, theme: { colors, spaces, breakpoints } },
) => ({
  ...DrawerStyle(open, colors, spaces, breakpoints),
  top: getDrawerTopPosition(open, spaces, true),
  maxHeight: `${spaces.large * 12.5}px`,
}))

const StyledHeader = styled.div(({ theme: { colors, spaces } }) => ({
  padding: `${spaces.default / 4}px ${spaces.default / 2}px ${spaces.default / 8}px`,
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `1px solid ${colors.light_grey}`,
}))

const StyledContent = styled.div(({ expanded, theme: { spaces, breakpoints } }) => ({
  height: '100%',
  boxSizing: 'border-box',
  overflowY: expanded ? 'auto' : 'hidden',
  padding: expanded ? `0 0 ${spaces.default}px` : `${spaces.default / 4}px ${spaces.default / 2}px ${spaces.default}px`,
  [`@media (min-width: ${breakpoints.medium})`]: {
    padding: expanded ? `0 0 ${spaces.default}px` : spaces.default,
  },
}))

const ExpandableDrawer = ({
  open: isOpen,
  mode,
  children,
  closeLabel,
  expandLabel,
  reduceLabel,
  isWebView,
  onClose = () => false,
  onModeChange = () => false,
}) => {
  const [open, setOpen] = useState(isOpen)
  const [expanded, setExpanded] = useState(mode === EXPANDABLE_DRAWER_MODE_EXPANDED)

  const [, setContentRef] = useContext(ExpandableDrawerContext)
  const drawerContentRef = useRef(null)

  useLayoutEffect(() => {
    setContentRef(drawerContentRef.current)
  }, null)

  useEffect(() => {
    setOpen(isOpen)
  }, [isOpen])

  useEffect(() => {
    /**
     * The Drawer Overlay doesn't prevent the scroll of the document body,
     * even when the expanded mode is on.
     * To prevent this behavior, we block the document body scroll
     * altogether when the Drawer is open and in expanded mode
     */
    const bodyOverflow = isOpen && expanded ? 'hidden' : null
    document.body.style.overflow = bodyOverflow
  }, [isOpen, expanded])

  useEffect(() => {
    setExpanded(mode === EXPANDABLE_DRAWER_MODE_EXPANDED)
  }, [mode])

  const isExpanded = open && expanded
  const isPreview = open && !expanded

  const StyledRootComponent = isExpanded ? StyledRoot : StyledRootPreview

  const gesturesConfig = {
    preventScrollOnSwipe: true,
  }

  const expandDrawer = action => {
    setExpanded(true)
    onModeChange(EXPANDABLE_DRAWER_MODE_EXPANDED, action)
  }

  const closeDrawer = () => {
    setOpen(false)
    onClose()
  }

  const reduceDrawer = action => {
    setOpen(true)
    onModeChange(EXPANDABLE_DRAWER_MODE_PREVIEW, action)
  }

  const PreviewHandlers = useSwipeable({
    onSwipedUp: () => expandDrawer(EXPANDABLE_DRAWER_ACTION_SWIPE),
    ...gesturesConfig,
  })

  const HeaderGesturesHandlers = useSwipeable({
    onSwipedDown: () => reduceDrawer(EXPANDABLE_DRAWER_ACTION_SWIPE),
    ...gesturesConfig,
  })

  /**
   * The SwipeDown gesture is handled only by the Drawer header (when the Drawer is Expanded)
   * so as not get into conflict with any swipeable component passed as children.
   * The Drawer expects the preview mode children to be simple text/buttons/images components
   */
  return (
    <>
      <StyledOverlay
        active={isExpanded}
        onClick={closeDrawer}
        data-test="DrawerOverlay"
      />
      <StyledRootComponent
        {...(isPreview ? PreviewHandlers : {})}
        open={open}
        isWebView={isWebView}
        data-test="ExpandableDrawer"
      >
        <StyledHeader {...(isExpanded ? HeaderGesturesHandlers : {})}>
          { isExpanded ? (
            <ModeToggleCTA
              onClick={() => reduceDrawer(EXPANDABLE_DRAWER_ACTION_CTA)}
              data-test="ExpandableDrawerReduce"
              direction="down"
              label={reduceLabel}
            />
          ) : (
            <ModeToggleCTA
              onClick={() => expandDrawer(EXPANDABLE_DRAWER_ACTION_CTA)}
              data-test="ExpandableDrawerExpand"
              direction="up"
              label={expandLabel}
            />
          ) }
          <CloseCTA
            onClick={closeDrawer}
            label={closeLabel}
            data-test="ExpandableDrawerClose"
          />
        </StyledHeader>
        <StyledContent data-test="ExpandableDrawerContent" expanded={isExpanded} ref={drawerContentRef}>
          {open ? children : null}
        </StyledContent>
      </StyledRootComponent>
    </>
  )
}

ExpandableDrawer.propTypes = {
  open: PropTypes.bool,
  isWebView: PropTypes.bool,
  mode: PropTypes.string,
  children: PropTypes.element,
  closeLabel: PropTypes.string,
  expandLabel: PropTypes.string,
  reduceLabel: PropTypes.string,
  onClose: PropTypes.func,
  onModeChange: PropTypes.func,
}

ExpandableDrawer.defaultProps = {
  open: false,
  isWebView: false,
  mode: EXPANDABLE_DRAWER_MODE_PREVIEW,
  children: null,
  closeLabel: '',
  expandLabel: '',
  reduceLabel: '',
  onClose: () => false,
  onModeChange: () => false,
}

ExpandableDrawer.displayName = 'ExpandableDrawer'

export default ExpandableDrawer
